import { HttpClient, HttpHeaders } from "@angular/common/http";
import { DomSanitizer } from '@angular/platform-browser';
import { PropertyById } from "app/shared/models/property.models";
import { Observable, forkJoin, of } from "rxjs";
import { catchError, map } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import * as api from '../../shared/api/apihandler';
import { ServiceDetails } from "app/service/service-details/serviceFunctions";
import { IdName } from "app/shared/models/standard.models";

export class CaseFilesFunctions {

    // Extract folders
    static remapObject(object): Folder[] {
        let remappedObject = object.map(folder => {
            let orphanFolder = (!!folder.parentFolderId && !!object.find(x => x.folderId == folder.parentFolderId)) || !folder.parentFolderId ? false : true;
            return {
                folderId: folder.folderId,
                parentFolderId: orphanFolder ? null : folder.parentFolderId,
                folderName: folder.folderName,
                fileAccess: folder.fileAccess
            };
        });
        return remappedObject;
    }

    // Extracting all the files
    static extractFiles(object: any[]): File[] {
        const files = [];
        object.forEach(folder => {
            const parentFolderId = !!folder.folderId ? folder.folderId : folder.id;
            folder.files.forEach(file => {
                files.push({
                    ...file,
                    parentFolderId
                });
            });
        });
        return files;
    }

    static joinFilesAndFolders(files: File[], folders: Folder[], openFolders: string[]): FileFolder[] {
        return files.filter(Boolean).map(x => {
            return {
                id: x.fileId,
                parentId: x.parentFolderId,
                name: x.originalName,
                typeId: x.fileTypeId,
                extension: x.extension,
                path: null,
                fileAccess: x.fileAccess,
                type: 'file',
                count: 0,
                selected: false
            }
        }).concat(
            folders.filter(Boolean).map(x => {
                return {
                    id: x.folderId,
                    parentId: x.parentFolderId,
                    name: x.folderName,
                    typeId: null,
                    extension: null,
                    path: null,
                    fileAccess: x.fileAccess,
                    type: 'folder',
                    count: 0,
                    selected: false,
                    open: openFolders.includes(x.folderId) ? true : false
                }
            })
        )
    }

    static countFilesInFolder(files: FileFolder[], id: string): number {
        let count = files.filter(x => x.parentId == id).length;
        let folders = files.filter(x => x.type == 'folder' && x.parentId == id);
        for (let x of folders) { count += this.countFilesInFolder(files, x.id) }
        return count
    }

    static getSubFoldersAndFiles(files: FileFolder[], id: string) {
        let subFiles = files.filter(x => x.parentId == id);
        subFiles.filter(x => x.type == 'folder').forEach(x => subFiles.concat(this.getSubFoldersAndFiles(files, x.id)))
        return subFiles
    }

    static breadCrumb(files: FileFolder[], folderId: string) {
        if (!folderId) return null
        let folder = files.find(x => x.id == folderId);
        let crumbs = [folder];
        if (!!folder && folder.parentId) crumbs.concat(this.breadCrumb(files, folderId));
        return crumbs

    }

    static checkCurrentSelected(current: FileFolder[]) {
        let selected = current.reduce((total, current) => total = total + (current.selected == true ? 1 : 0), 0);
        return selected == current.length && current.length > 0 ? 'all' : selected > 0 && selected < current.length ? 'some' : 'none';
    }

    static getBranch(files: FileFolder[], id: string) {
        let arr = [];
        let obj = files.find(o => o.id === id);
        while (obj && !!obj.parentId) {
            arr.push(obj.id);
            obj = files.find(o => o.id === obj!.parentId);
        }
        return arr.reverse();
    }

    async convertOSFiles(files: { files: OSFile[], docs: OSDocument[] }, sanitize: DomSanitizer): Promise<{ files: any[], docs: any[] }> {
        let d = files.docs.map(x => {
            return { ...x, selected: false, baseString: x.base64, fileType: '71E32E6A-FF53-4CE2-9704-1C9FC94212F1', fileName: x.fileName + '.pdf', extension: 'pdf' }
        });
        let a = [];
        for (let f of files.files) {
            let ext = f.filename.split('.').reverse()[0] as string;
            let end = this.fileIdToExt[ext];
            if (!end) { continue }
            let base = await sanitize.bypassSecurityTrustResourceUrl('data:' + end.mime + ';base64, ' + f.fileContent);
            a.push(
                {
                    fileName: f.filename,
                    extension: ext,
                    fileType: end.id,
                    base64: base,
                    baseString: f.fileContent,
                    selected: false
                }
            )
        }
        return { files: a, docs: d }
    }

    fileIdToExt = {
        'jpg': { id: 'C4AE7891-6179-40FE-9F53-FB52F0889ABC', mime: 'image/jpeg' },
        'jpeg': { id: 'C4AE7891-6179-40FE-9F53-FB52F0889ABC', mime: 'image/jpeg' },
        'png': { id: 'C1B89127-CF88-4477-BA7B-6040B0B8B5A6', mime: 'image/png' },
        'pdf': { id: '71E32E6A-FF53-4CE2-9704-1C9FC94212F1', mime: 'application/pdf' },
        'xlsx': { id: '070CD1BE-60C1-479F-A38C-AAB603DD8D11', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
        'xls': { id: '69E80CCE-7FF3-42C1-92CE-75137E557F94', mime: 'application/excel' },
        'heic': { id: 'D901D49C-A6EA-476F-A126-41C0317C0780', mime: 'image/heic' },
        'pptx': { id: '7EE21B48-DAE1-48C6-929F-E1BFEF4AB828', mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' }
    }

    fileExtToId = {
        'image/jpeg': 'C4AE7891-6179-40FE-9F53-FB52F0889ABC',
        'image/png': 'C1B89127-CF88-4477-BA7B-6040B0B8B5A6',
        'application/pdf': '71E32E6A-FF53-4CE2-9704-1C9FC94212F1',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '71E32E6A-FF53-4CE2-9704-1C9FC94212F1',
        'application/excel': '69E80CCE-7FF3-42C1-92CE-75137E557F94',
        'image/heic': 'D901D49C-A6EA-476F-A126-41C0317C0780',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx'
    }
}

export class FileFunctions {
    bearer = localStorage.getItem('bearer') as string;
    constructor(private http: HttpClient) { }
    fileExtToId = {
        'image/jpeg': 'C4AE7891-6179-40FE-9F53-FB52F0889ABC',
        'image/png': 'C1B89127-CF88-4477-BA7B-6040B0B8B5A6',
        'application/pdf': '71E32E6A-FF53-4CE2-9704-1C9FC94212F1',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '71E32E6A-FF53-4CE2-9704-1C9FC94212F1',
        'application/excel': '69E80CCE-7FF3-42C1-92CE-75137E557F94',
        'image/heic': 'D901D49C-A6EA-476F-A126-41C0317C0780',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx'
    }
    attachFileToCase(caseId: string, fileId: string, folderId: string): Observable<any> {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.post(api.jubloApi + 'api/shared/attachments/AttachFileToCase', { caseId: caseId, fileId: fileId, folderId: folderId }, { headers })
    }

    attachFileToProperty(propertyId: string, fileId: string, folderId: string): Observable<any> {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.post(api.jubloApi + 'api/shared/Attachments/AttachFileToProperty', { propertyId: propertyId, fileId: fileId, folderId: folderId }, { headers })
    }

    attachFileToAsset(assetId: string, file: string, folderId: string) {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.post(api.jubloApi + `api/shared/Attachments/AttachFileToAsset`, {
            assetId: assetId,
            fileId: file,
            folderId: folderId
        }, { headers })
    }

    attachFileToService(serviceId: string, file: string, folderId: string) {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.post(api.jubloApi + `api/shared/Attachments/AttachFileToService`, {
            serviceId: serviceId,
            fileId: file,
            folderId: folderId
        }, { headers })
    }

    uploadFilesToAsset(assetId: string, files: any[], folderId: string) {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return new Observable(obs => {
            const fileUploads = Array.from(files).map(file => this.fileWithPath(file, folderId));
            forkJoin(fileUploads).subscribe({
                next: (data) => {
                    let ids = data.map(x => x.fileId);
                    this.http.post(api.jubloApi + `api/Assets/UploadFileToAsset`, {
                        assetId: assetId,
                        files: ids
                    }, { headers }).subscribe({
                        complete: () => obs.complete()
                    })
                }
            })
        });
    }

    filesToAsset(assetId: string, files: string[], folderId: string) {
        const uploads = files.map(x => this.attachFileToAsset(assetId, x, folderId));
        return forkJoin(uploads)
    }

    filesToService(serviceId: string, files: string[], folderId: string) {
        const uploads = files.map(x => this.attachFileToService(serviceId, x, folderId));
        return forkJoin(uploads)
    }

    attachFileToFolder(caseId: string, fileId: string, folderId: string, copy: boolean) {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.post(api.jubloApi + 'api/shared/attachments/CopyFileToCase', {
            caseId: caseId,
            fileId: fileId,
            folderId: folderId,
            copy: copy
        }, { headers })
    }

    fileWithPath(file: any, path: string | null): Observable<any> {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        let input = new FormData();
        input.append('file', file);
        let fileAccessId = '4D32156A-20FE-4601-8CC8-0A4711034D6B';
        return this.http.post(api.jubloApi + 'api/shared/attachments/FileWithPath?fileAccessId=' + fileAccessId + (!!path ? '&path=' + path : ''), input);
    }

    filesUpload(files: File[]) {
        let arr = files.map(file => {
            return this.fileWithPath(file, null)
        })
    }

    fileToTask(fileId: string, taskId: string) {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.post(api.jubloApi + 'api/Activity/FileToActivity', {
            activityId: taskId,
            fileId: fileId
        }, { headers })
    }

    oldFileUploadToCase(caseId: string, folderId: string, file: File) {
        return new Observable(obs => this.fileWithPath(file, null).subscribe({
            next: (file: any) => {
                this.attachFileToCase(caseId, file.fileId, folderId).subscribe({
                    next: () => {
                        obs.next('done');
                        obs.complete();
                    },
                    error: (err) => {
                        obs.next(err);
                        obs.complete()
                    }
                })
            },
            error: (err) => {
                obs.next(err);
                obs.complete()
            }
        }))
    }

    oldFileUploadToProperty(propertyId: string, folderId: string, file: File) {
        return new Observable(obs => this.fileWithPath(file, null).subscribe({
            next: (file: any) => {
                this.attachFileToProperty(propertyId, file.fileId, folderId).subscribe({
                    next: () => {
                        obs.next('done');
                        obs.complete();
                    },
                    error: (err) => {
                        obs.next(err);
                        obs.complete()
                    }
                })
            },
            error: (err) => {
                obs.next(err);
                obs.complete()
            }
        }))
    }

    oldFileUploadToAsset(assetId: string, files: any[], folderId: string): Observable<Observable<any>> {
        return new Observable(obs => {
            const fileUploads = Array.from(files).map(file => this.fileWithPath(file, folderId));
            forkJoin(fileUploads).subscribe({
                next: (data: any) => {
                    let ids = data.map(x => x.fileId)
                    this.filesToAsset(assetId, ids, folderId).subscribe({
                        complete: () => obs.complete()
                    });
                },
                error: (err) => {
                    console.error('Something went wrong with the file upload', err)
                    obs.complete()
                }
            })

        })
    }

    oldFileUploadToService(serviceId: string, files: any[], folderId: string): Observable<Observable<any>> {
        return new Observable(obs => {
            const fileUploads = Array.from(files).map(file => this.fileWithPath(file, folderId));
            forkJoin(fileUploads).subscribe({
                next: (data: any) => {
                    console.log('files uploaded', data)
                    let ids = data.map(x => x.fileId)
                    this.filesToService(serviceId, ids, folderId).subscribe({
                        complete: () => obs.complete()
                    });
                },
                error: (err) => {
                    console.error('Something went wrong with the file upload', err)
                    obs.complete()
                }
            })

        })
    }

    removeFilesFromCase(caseId: string, selection: string[]): Observable<any> {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.post<string[]>(api.jubloApi + 'api/Website/Case/RemoveFileFromCase/' + caseId, selection, { headers })
    }

    removeFolderFromCase(caseId: string, folderId: string) {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.put(api.jubloApi + 'api/shared/Attachments/DeleteFolder', { caseId: caseId, folderId: folderId }, { headers })
    }

    removeFilesFromProperty(propertyId: string, selection: string[]) {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.put(api.jubloApi + 'api/shared/Attachments/RemoveFilesFromProperty', {
            propertyId: propertyId,
            files: selection
        }, { headers })
    }

    removeFolderFromProperty(propertyId: string, folderId: string) {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.put(api.jubloApi + 'api/shared/Attachments/DeletePropertyFolder', {
            propertyId: propertyId,
            folderId: folderId
        }, { headers })
    }

    removeFilesFromAsset(assetId: string, selection: string[]): Observable<{ assetId: string, files: string[] }> {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.put<{ assetId: string, files: string[] }>(api.jubloApi + `api/shared/Attachments/RemoveFilesFromAsset`, {
            assetId: assetId,
            files: selection
        }, { headers })
    }

    deleteFolderFromAsset(assetId: string, folderId: string) {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.put(api.jubloApi + `api/shared/Attachments/DeleteAssetFolder`, {
            assetId: assetId,
            folderId: folderId
        }, { headers })
    }

    removeFoldersFromAsset(assetId: string, folders: string[]) {
        const deletables = folders.map(x => this.deleteFolderFromAsset(assetId, x));
        return forkJoin(deletables)
    }

    folderToCase(caseId: string, folder: FileFolder): Observable<any> {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.post(api.jubloApi + 'api/shared/Attachments/FolderToCase', {
            caseId: caseId,
            folderId: null,
            parentFolder: folder.parentId,
            folderName: folder.name,
            fileAccessId: folder.fileAccess
        }, { headers })
    }

    folderToProperty(propertyId: string, folder: FileFolder): Observable<any> {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.post(api.jubloApi + 'api/shared/Attachments/FolderToProperty', {
            propertyId: propertyId,
            folderId: null,
            parentFolder: folder.parentId,
            folderName: folder.name,
            fileAccessId: folder.fileAccess
        }, { headers })
    }

    folderToAsset(assetId, folder: FileFolder): Observable<any> {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.post(api.jubloApi + `api/shared/Attachments/FolderToAsset`, {
            assetId: assetId,
            parentFolder: folder.parentId,
            folderName: folder.name,
            fileAccesId: folder.fileAccess
        }, { headers })
    }

    folderToService(serviceId, folder: FileFolder): Observable<any> {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.post(api.jubloApi + `api/shared/Attachments/FolderToService`, {
            serviceId: serviceId,
            parentFolder: folder.parentId,
            folderName: folder.name,
            fileAccesId: folder.fileAccess
        }, { headers })
    }

    duplicateFolders(caseId: string, folders: FileFolder[]): Observable<object> {
        return new Observable((observer) => {
            let ob = {};
            let count = 0;
            for (let x of folders) {
                let current = { ...x, id: null, parentId: null }
                this.folderToCase(caseId, current).subscribe({
                    next: (response) => {
                        ob[x.id] = (typeof response == 'string') ? response : response.success;
                    },
                    error: () => { count += 1; },
                    complete: () => {
                        count += 1;
                        if (count == folders.length) {
                            observer.next(ob);
                            observer.complete()
                        }
                    }
                })
            }

        })
    }

    fileToCase(caseId: string, folderId: string, file: FormData) {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        let data = {
            caseId: caseId,
            path: null,
            fileAccessId: '4D32156A-20FE-4601-8CC8-0A4711034D6B',
            folderId: folderId,
            file: file
        };
        console.log(data)
        return this.http.post(api.jubloApi + 'api/shared/Attachments/UploadAttachFileToCase', data, { headers })
    }

    getFilesOnCase(caseId: string) {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.get(api.jubloApi + 'api/Website/Case/GetCaseFiles/' + caseId, { headers })
    }

    /**
     * 
     * @param propertyId 
     * @returns 
     */
    getFilesOnProperty(propertyId: string) {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.get(api.jubloApi + 'api/PropertyLease/GetFilesOnProperty/' + propertyId, { headers })
    }

    getFilesOnTask(taskId: string) {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.get(api.jubloApi + 'api/Activity/GetFilesOnActivity/' + taskId, { headers })
    }

    getFilesOnAsset(assetId: string) {
        return new Observable(obs => {
            const headers = new HttpHeaders({ 'Authorization': this.bearer });
            this.http.get(api.jubloApi + 'api/Assets/AssetsById/' + assetId, { headers }).subscribe({
                next: (data: any) => {
                    // console.log(data)
                    // Only data.folders comes with files. data.files does not.
                    obs.next(data.folders)
                },
                error: (err) => obs.error(err),
                complete: () => obs.complete()
            })
        })
    }

    getFilesOnService(serviceId: string) {
        return new Observable(obs => {
            const headers = new HttpHeaders({ 'Authorization': this.bearer });
            this.http.get(api.jubloApi + 'api/Service/ServiceDetails/' + serviceId, { headers }).subscribe({
                next: (data: ServiceDetails) => obs.next(data.files),
                error: (err) => obs.error(err),
                complete: () => obs.complete()
            })
        })
    }

    getPropertyFilesWorkAround(propertyId: string) {
        return new Observable(obs => {
            this.getProperty(propertyId).subscribe({
                next: (prop: PropertyById) => {
                    this.getFilesOnProperty(propertyId).subscribe({
                        next: (files) => {
                            console.log(prop, files)
                            obs.next(
                                {
                                    filesWithoutFolder: files,
                                    files: prop.files.files
                                }
                            )
                            obs.complete()
                        }
                    })
                }
            })
        })
    }

    getProperty(propertyId: string) {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.get<PropertyById>(api.jubloApi + 'api/PropertyLease/PropertyByIdExtended/' + propertyId, { headers })
    }

    /**
     * 
     * @param caseId current case id
     * @param fileId of file to be moved
     * @param folderId target folder id
     * @param copy use if you want a file to be copied instead of moved
     * @returns an observable
     */
    moveFileToFolder(caseId, fileId, folderId, copy) {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.post(api.jubloApi + 'api/shared/attachments/CopyFileToCase', {
            caseId: caseId,
            fileId: fileId,
            folderId: folderId,
            copy: copy
        }, { headers })
    }

    moveFolderToFolder(folderId, targetFolderId) {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.patch(api.jubloApi + 'api/shared/Attachments/UpdateParentFolder', {
            folderId: folderId,
            parentFolderId: targetFolderId
        }, { headers })
    }

    moveAssetFolderToFolder(folderId: string, parentFolderId: string) {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.patch(api.jubloApi + 'api/shared/Attachments/UpdateAssetParentFolder', {
            folderId: folderId,
            parentFolderId: parentFolderId
        }, { headers })
    }

    collectDeletion(files: FileFolder[]) {
        let selection = files.filter(x => !!x.selected);
        if (!selection.find(x => x.type == 'folder')) {
            return {
                folders: [],
                files: selection
            }
        }
        else {
            // let increasedSelection = this.findSubFiles(files, files.filter(x => x.type == 'folder' && !!x.selected));
        }
    }

    findSubFolders(folderId: string, files: FileFolder[]) {
        let output = []
        let subFolders = files.filter(x => x.type == 'folder' && x.parentId == folderId).map(x => x.id);
        if (!!subFolders.length) subFolders.forEach(x => output.concat(this.findSubFolders(x, files)));
        return output

    }

    deleteFiles(caseId: string, selection: string[]) {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.post(api.jubloApi + 'api/Website/Case/RemoveFileFromCase/' + caseId, selection, { headers })
    }

    getOSFiles(caseId: string) {
        return new Observable(obs => {
            let files: any[] = null;
            let docs: any[] = null;
            const headers = new HttpHeaders({ 'Authorization': this.bearer });
            this.http.get(api.jubloApi + 'api/Website/Case/GetOsDocuments/' + caseId, { headers }).subscribe({
                next: (data: any) => {
                    if (!!data) files = data.documents.sort((a, b) => a.createdAt < b.createdAt ? 1 : -1);
                    else files = [];
                },
                error: (err) => { files = [] },
                complete: () => {
                    if (!!files && !!docs) {
                        obs.next({
                            files: files,
                            docs: docs
                        });
                        obs.complete()
                    }
                }
            });
            this.http.get(api.jubloApi + 'api/Website/Case/GetKvalitetsSikring/' + caseId, { headers }).subscribe({
                next: (data: any[]) => {
                    if (!!data) docs = data;
                    else docs = [];
                },
                error: (err) => { docs = [] },
                complete: () => {
                    if (!!files && !!docs) {
                        obs.next({
                            files: files,
                            docs: docs
                        });
                        obs.complete()
                    }
                }
            })
        })
    }

    filesToOS(typeId: string, selection: string[]) {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.post(api.jubloApi + 'api/Website/Case/FilesToOS/' + typeId, selection, { headers })
    }

    filesFromOs(caseId: string, file: { fileName: string, fileType: string, base64: string }) {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.post(api.jubloApi + 'api/Website/Case/AddFileFromOS', {
            caseId: caseId,
            fileName: file.fileName,
            fileTypeId: file.fileType,
            base64: file.base64
        }, { headers })
    }

    importFilesFromOs(caseId: string, files: { fileName: string, fileType: string, base64: string }[], targetFolderId: string) {
        return new Observable((obs) => {
            const length = files.length;
            let count = 0;
            for (let f of files) {
                this.filesFromOs(caseId, f).subscribe({
                    next: (data: { extension: string, fileId: string, fileTypeId: string, originalName: string }) => {
                        this.moveFileToFolder(caseId, data.fileId, targetFolderId, false).subscribe({
                            next: () => { count += 1 },
                            error: (err) => { count += 1 },
                            complete: () => {
                                if (count == length) {
                                    obs.complete()
                                }
                            }
                        })
                    },
                    error: (err) => {
                        count += 1;
                    },
                    complete: () => {
                        if (count == length) {
                            obs.complete()
                        }
                    }
                })
            }

        })
    }

    private upload(fileToUpload: any, path: string = '') {
        let input = new FormData();
        input.append('file', fileToUpload);
        let fileAccessId = '4D32156A-20FE-4601-8CC8-0A4711034D6B';
        return this.http.post(api.jubloApi + 'api/shared/attachments/FileWithPath?fileAccessId=' + fileAccessId + '&path=' + path, input);
    }

    uploadFile(f) {
        return new Observable((obs) => {
            this.upload(f).subscribe({
                next: (data: any) => {
                    obs.next({
                        extension: '.' + data.ext,
                        fileAccess: '4d32156a-20fe-4601-8cc8-0a4711034d6b',
                        fileId: data.fileId,
                        fileTypeId: this.fileExtToId[f.type],
                        originalName: data.fileName,
                        name: data.fileName,
                        path: null
                    })
                },
                error: (err) => { obs.error('Could not upload file'); console.warn("Could not upload file", f, err) },
                complete: () => obs.complete()
            })
        })
    }

    fileEventToExcelData(event): { name: string, book: XLSX.WorkBook }[] {
        console.log(event)
        let files = Array.from(event) as any[];
        let output = files.map((f) => {
            console.log(f)
            let wb: XLSX.WorkBook;

            return { name: f.name, book: wb }
        })
        return output
    }

    getExcelData(event: FileList): Observable<{ name: string, book: XLSX.WorkBook }[]> {
        let output = [];
        return new Observable(obs => {
            for (let f of Array.from(event)) {
                let wb: XLSX.WorkBook;
                const reader: FileReader = new FileReader();
                reader.readAsBinaryString(f);
                reader.onload = (e: any) => {
                    /* create workbook */
                    const binaryStr: string = e.target.result;
                    wb = XLSX.read(binaryStr, { type: 'binary' });
                    output.push({ name: f.name, book: wb });
                    if (output.length == event.length) {
                        obs.next(output);
                        obs.complete()
                    }
                }
            }
        })
    }

    renameFolder(folder: IdName) {
        const headers = new HttpHeaders({ 'Authorization': this.bearer });
        return this.http.patch(api.jubloApi + `api/shared/Attachments/RenameFolder`, {
            folderId: folder.id,
            folderName: folder.name
        }, { headers })
    }

    // Files upload automation
    /**
     * 
     * @param dt event.DataTransfer from file input.
     * @param parentFolder folder to start the upload in.
     * @returns Promise<UploadCounter>
     */
    async uploadFileCount(dt: DataTransfer, parentFolder: string | null): Promise<UploadCounter> {
        const uploadCounter = new UploadCounter();
        const items = dt.items;
        for (let i = 0; i < items.length; i++) {
            const entry = items[i].webkitGetAsEntry();
            if (entry) await this.countEntries(entry, uploadCounter, parentFolder);
        }
        return uploadCounter
    }

    private async countEntries(entry: any, uploadCounter: UploadCounter, parentFolder) {
        return new Promise<void>(async (resolve) => {
            if (entry.isFile) {
                entry.file(file => uploadCounter.files.push({ file: file, parent: parentFolder }))
                uploadCounter.fileCount = uploadCounter.files.length;
                resolve();
            }
            else if (entry.isDirectory) {
                uploadCounter.folders.push({ folder: entry, parent: parentFolder })
                uploadCounter.folderCount = uploadCounter.folders.length;
                const directorReader = entry.createReader();
                directorReader.readEntries(async (entries: any[]) => {
                    for (const subEntry of entries) {
                        await this.countEntries(subEntry, uploadCounter, entry.name);
                    }
                    resolve();
                })
            }
        })
    }

    async uploadTo(is: { type: FileTargetType, id: string }, files: { file: File, parent: string | null }[], parentFolderId: string): Promise<{ success: any[], fail: any[] }> {
        return new Promise(resolve => {
            switch (is.type) {
                case 'case': {
                    this.caseUploader(files, is.id, parentFolderId).subscribe(result => {
                        resolve(result);
                    });
                    break;
                }
                case 'property': {
                    this.propertyUploader(files, is.id, parentFolderId).subscribe(result => {
                        resolve(result);
                    });
                    break;
                }
                // Add similar blocks for 'property', 'asset', 'service', etc.
            }
        });
    }

    async uploadFoldersTo(is: { type: FileTargetType, id: string }, folders, parentFolderId: string): Promise<{ folder: FileFolder, id: string }[]> {
        switch (is.type) {
            case 'case': {
                let folderObservables = folders.map(x => { return this.caseFolderUploader(is.id, x) });
                return new Promise((resolve, reject) => {
                    forkJoin(folderObservables).subscribe({
                        next: (data: any[]) => {
                            resolve(data);
                        },
                        error: (err) => {
                            reject(err);
                        }
                    });
                })
                break;
            }
            case 'asset': { }
            case 'property': {
                let folderObservables = folders.map(x => { return this.propertyFolderUploader(is.id, x) });
                return new Promise((resolve, reject) => {
                    forkJoin(folderObservables).subscribe({
                        next: (data: any[]) => {
                            resolve(data);
                        },
                        error: (err) => {
                            reject(err);
                        }
                    });
                })
            }
            case 'service': { }
        }
    }

    private caseUploader(files: { file: File, parent: string | null }[], id: string, parentFolderId: string): Observable<{ success: any[], fail: any[] }> {
        let succeeded = [];
        let failed = [];

        const uploadObservables = files.map((file: { file: File, parent: string | null }) => {
            return this.uploadFile(file.file).pipe(
                catchError(error => {
                    failed.push({ name: file.file.name });
                    return of(null);
                }),
                map(response => {
                    if (response) {
                        succeeded.push({ name: file.file.name, id: response.fileId });
                    }
                    return response;
                })
            );
        });

        return new Observable(observer => {
            forkJoin(uploadObservables).subscribe({
                next: (uploadedFiles) => {
                    console.log(uploadedFiles)
                    uploadedFiles = uploadedFiles.filter(file => file !== null);
                    const attachObservables = uploadedFiles.map(file => {
                        return this.attachFileToCase(id, file.fileId, parentFolderId).pipe(
                            catchError(error => {
                                return of(null);
                            })
                        );
                    });

                    forkJoin(attachObservables).subscribe({
                        complete: () => {
                            observer.next({ success: succeeded, fail: failed });
                            observer.complete();
                        }
                    });
                }
            });
        });
    }

    private caseFolderUploader(caseId: string, folder: FileFolder): Observable<{ folder: FileFolder, id: string }> {
        return new Observable(observer => {
            this.folderToCase(caseId, folder).subscribe({
                next: (response) => {
                    const id = (typeof response === 'string') ? response : response.success;
                    observer.next({ folder, id });
                    observer.complete();
                },
                error: (err) => {
                    observer.error(err);
                }
            });
        });
    }

    private propertyUploader(files: { file: File, parent: string | null }[], id: string, parentFolderId: string): Observable<{ success: any[], fail: any[] }> {
        let succeeded = [];
        let failed = [];

        const uploadObservables = files.map((file: { file: File, parent: string | null }) => {
            return this.uploadFile(file.file).pipe(
                catchError(error => {
                    failed.push({ name: file.file.name });
                    return of(null);
                }),
                map(response => {
                    if (response) {
                        succeeded.push({ name: file.file.name, id: response.fileId });
                    }
                    return response;
                })
            );
        });

        return new Observable(observer => {
            forkJoin(uploadObservables).subscribe({
                next: (uploadedFiles) => {
                    uploadedFiles = uploadedFiles.filter(file => file !== null);
                    const attachObservables = uploadedFiles.map(file => {
                        return this.attachFileToProperty(id, file.fileId, parentFolderId).pipe(
                            catchError(error => {
                                return of(null);
                            })
                        );
                    });

                    forkJoin(attachObservables).subscribe({
                        complete: () => {
                            observer.next({ success: succeeded, fail: failed });
                            observer.complete();
                        }
                    });
                }
            });
        });
    }

    private propertyFolderUploader(propertyId: string, folder: FileFolder): Observable<{ folder: FileFolder, id: string }> {
        return new Observable(observer => {
            this.folderToProperty(propertyId, folder).subscribe({
                next: (response) => {
                    const id = (typeof response === 'string') ? response : response.success;
                    observer.next({ folder, id });
                    observer.complete();
                },
                error: (err) => {
                    observer.error(err);
                }
            });
        });
    }

}
type FileTargetType = 'case' | 'property' | 'asset' | 'service';
export class FileFolder {
    id: string;
    parentId: string | null;
    name: string;
    typeId: string;
    extension: string;
    path: string | null;
    fileAccess: string;
    type: string;
    count: number;
    selected: boolean;
}

interface Folder {
    folderId: string;
    parentFolderId: string | null;
    folderName: string;
    fileAccess?: string;
}

export interface File {
    fileId: string;
    originalName: string;
    fileTypeId: string;
    extension: string;
    path: string | null;
    fileAccess: string;
    parentFolderId: string;
    name?: string;
}

export interface OSFile {
    createtAt: number;
    fileContent: string;
    fileUrl: string;
    filename: string;
}

export interface OSDocument {
    fileName: string;
    base64: string;
}

export class UploadCounter {
    folderCount: number = 0;
    fileCount: number = 0;
    uploadedFolders: number = 0;
    uploadedFiles: number = 0;
    folders: any[] = [];
    files: any[] = [];
}
